import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Users,
  Search,
  Monitor,
  Key,
  Database,
  CheckCircle,
  XCircle,
  AlertTriangle,
  HardDrive,
  Cpu,
  MemoryStick,
  ChevronRight,
  Shield,
  ShieldAlert,
  Clock,
  Plus,
  RefreshCw,
  Trash2,
  Edit,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import {
  getPeople,
  getDevices,
  getBackups,
  getDeviceHealth,
  createPerson,
  updatePerson,
  deletePerson,
  checkPersonBreach,
} from "../lib/api";

export const PeoplePage = () => {
  const [people, setPeople] = useState([]);
  const [devices, setDevices] = useState([]);
  const [backups, setBackups] = useState([]);
  const [deviceHealth, setDeviceHealth] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingPerson, setEditingPerson] = useState(null);
  const [checkingBreach, setCheckingBreach] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [peopleRes, devicesRes, backupsRes, healthRes] = await Promise.all([
        getPeople(),
        getDevices(),
        getBackups(),
        getDeviceHealth(),
      ]);

      setDevices(devicesRes.items);
      setBackups(backupsRes.items);
      setDeviceHealth(healthRes.items);

      // Enrich people with device/backup/health data
      const enrichedPeople = peopleRes.items.map((person) => {
        const personDevices = devicesRes.items.filter((d) => {
          const deviceName = d.name.toLowerCase();
          const personName = person.user_name.toLowerCase().split(" ")[0];
          return (
            deviceName.includes(personName) ||
            deviceName.includes("workstation") ||
            deviceName.includes("laptop")
          );
        });

        const personBackups = backupsRes.items.filter((b) =>
          personDevices.some((d) => d.id === b.device_id),
        );
        const personHealth = healthRes.items.filter((h) =>
          personDevices.some((d) => d.id === h.device_id),
        );

        return {
          ...person,
          name: person.user_name,
          email: person.user_email,
          passwordHealth: person,
          devices: personDevices.slice(0, 2),
          backups: personBackups,
          health: personHealth,
        };
      });

      setPeople(enrichedPeople);
    } catch (err) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: formData.name,
        email: formData.email,
        password_age_days: editingPerson?.password_age_days ?? 0,
        password_strength: editingPerson?.strength ?? "medium",
      };
      if (editingPerson) {
        await updatePerson(editingPerson.id, payload);
        toast.success("Person updated successfully");
      } else {
        await createPerson(payload);
        toast.success("Person added successfully");
      }
      fetchData();
      handleCloseDialog();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save person");
    }
  };

  const handleDelete = async (personId) => {
    if (!window.confirm("Are you sure you want to remove this person?")) return;
    try {
      await deletePerson(personId);
      toast.success("Person removed successfully");
      setSelectedPerson(null);
      fetchData();
    } catch (err) {
      toast.error("Failed to remove person");
    }
  };

  const handleEdit = (person) => {
    setEditingPerson(person);
    setFormData({
      name: person.user_name,
      email: person.user_email,
    });
    setShowAddDialog(true);
  };

  const handleCheckBreach = async (personId) => {
    setCheckingBreach(personId);
    try {
      const response = await checkPersonBreach(personId);
      if (response.data?.is_leaked) {
        toast.error(
          `Breach found! ${response.data.message || "Email found in breaches"}`,
        );
      } else if (response.data?.checked) {
        toast.success(response.data.message || "No breach found");
      } else {
        toast.info(response.data.message);
      }
      fetchData();
    } catch (err) {
      toast.error("Failed to check breach status");
    } finally {
      setCheckingBreach(null);
    }
  };

  const handleCloseDialog = () => {
    setShowAddDialog(false);
    setEditingPerson(null);
    setFormData({
      name: "",
      email: "",
    });
  };

  const getInitials = (name) => {
    return (
      name
        ?.split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase() || "U"
    );
  };

  const getOverallStatus = (person) => {
    const issues = [];
    if (person.passwordHealth?.is_leaked) issues.push("password_leaked");
    if (
      person.passwordHealth?.risk_level === "high" ||
      person.passwordHealth?.risk_level === "critical"
    )
      issues.push("password_weak");
    if (person.backups?.some((b) => b.status === "failed"))
      issues.push("backup_failed");
    if (person.health?.some((h) => h.health_status === "critical"))
      issues.push("device_critical");
    if (person.health?.some((h) => h.health_status === "warning"))
      issues.push("device_warning");

    if (
      issues.includes("password_leaked") ||
      issues.includes("device_critical")
    )
      return "critical";
    if (issues.length > 0) return "warning";
    return "healthy";
  };

  const filteredPeople = people.filter(
    (person) =>
      person.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      person.email?.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  if (loading) {
    return (
      <div
        className="flex items-center justify-center h-64"
        data-testid="people-loading"
      >
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="people-page">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-foreground">
            People
          </h1>
          <p className="text-muted-foreground mt-1">
            View each person's devices, passwords, and security status
          </p>
        </div>
        <Button
          onClick={() => setShowAddDialog(true)}
          data-testid="add-person-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Person
        </Button>
      </div>

      {/* Search */}
      <Card className="border-border shadow-sm">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search people by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
              data-testid="people-search-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* People Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredPeople.map((person) => {
          const status = getOverallStatus(person);
          return (
            <Card
              key={person.id}
              className={`border-border shadow-sm cursor-pointer transition-all hover:shadow-md ${
                status === "critical"
                  ? "border-red-300 bg-red-50/50"
                  : status === "warning"
                    ? "border-amber-300 bg-amber-50/50"
                    : ""
              }`}
              onClick={() => setSelectedPerson(person)}
              data-testid={`person-card-${person.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start gap-4">
                  <Avatar className="h-12 w-12">
                    <AvatarFallback
                      className={`text-white ${
                        status === "critical"
                          ? "bg-red-500"
                          : status === "warning"
                            ? "bg-amber-500"
                            : "bg-green-500"
                      }`}
                    >
                      {getInitials(person.name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold truncate">{person.name}</h3>
                      {status === "critical" && (
                        <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                      )}
                      {status === "warning" && (
                        <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0" />
                      )}
                      {status === "healthy" && (
                        <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground truncate">
                      {person.email}
                    </p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                </div>

                {/* Quick Status Icons */}
                <div className="flex items-center gap-4 mt-4 pt-4 border-t">
                  <div
                    className="flex items-center gap-1.5"
                    title="Password Health"
                  >
                    <Key
                      className={`w-4 h-4 ${
                        person.passwordHealth?.is_leaked
                          ? "text-red-500"
                          : person.passwordHealth?.risk_level === "high"
                            ? "text-amber-500"
                            : "text-green-500"
                      }`}
                    />
                    <span className="text-xs text-muted-foreground">
                      {person.passwordHealth?.is_leaked
                        ? "Leaked"
                        : person.passwordHealth?.risk_level === "high"
                          ? "Weak"
                          : "OK"}
                    </span>
                  </div>

                  <div
                    className="flex items-center gap-1.5"
                    title="Device Health"
                  >
                    <Monitor
                      className={`w-4 h-4 ${
                        person.health?.some(
                          (h) => h.health_status === "critical",
                        )
                          ? "text-red-500"
                          : person.health?.some(
                                (h) => h.health_status === "warning",
                              )
                            ? "text-amber-500"
                            : "text-green-500"
                      }`}
                    />
                    <span className="text-xs text-muted-foreground">
                      {person.devices?.length || 0} device
                      {person.devices?.length !== 1 ? "s" : ""}
                    </span>
                  </div>

                  <div
                    className="flex items-center gap-1.5"
                    title="Backup Status"
                  >
                    <Database
                      className={`w-4 h-4 ${
                        person.backups?.some((b) => b.status === "failed")
                          ? "text-red-500"
                          : "text-green-500"
                      }`}
                    />
                    <span className="text-xs text-muted-foreground">
                      {person.backups?.some((b) => b.status === "failed")
                        ? "Failed"
                        : "OK"}
                    </span>
                  </div>
                </div>

                {/* Password Leak Warning */}
                {person.passwordHealth?.is_leaked && (
                  <div className="mt-3 p-2 bg-red-100 border border-red-200 rounded-md">
                    <div className="flex items-center gap-2 text-red-800 text-sm font-medium">
                      <ShieldAlert className="w-4 h-4" />
                      Password Leaked
                    </div>
                    <p className="text-xs text-red-700 mt-1">
                      Found in: {person.passwordHealth.leak_source}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {filteredPeople.length === 0 && (
        <Card className="border-dashed">
          <CardContent className="p-8 text-center text-muted-foreground">
            <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No people found. Add your first person to start tracking.</p>
            <Button onClick={() => setShowAddDialog(true)} className="mt-4">
              <Plus className="w-4 h-4 mr-2" />
              Add Person
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Add/Edit Person Dialog */}
      <Dialog open={showAddDialog} onOpenChange={handleCloseDialog}>
        <DialogContent className="sm:max-w-md" data-testid="person-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingPerson ? "Edit Person" : "Add New Person"}
            </DialogTitle>
            <DialogDescription>
              {editingPerson
                ? "Update person information"
                : "Add a new person to track their security status"}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Full Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                placeholder="e.g., John Doe"
                required
                data-testid="person-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                placeholder="e.g., john@company.com"
                required
                data-testid="person-email-input"
              />
              <p className="text-xs text-muted-foreground">
                You can check this email for data breaches after adding the
                person.
              </p>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={handleCloseDialog}
              >
                Cancel
              </Button>
              <Button type="submit" data-testid="person-submit-btn">
                {editingPerson ? "Update" : "Add Person"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Person Detail Dialog */}
      <Dialog
        open={!!selectedPerson}
        onOpenChange={() => setSelectedPerson(null)}
      >
        <DialogContent
          className="max-w-2xl max-h-[90vh] overflow-y-auto"
          data-testid="person-detail-dialog"
        >
          {selectedPerson && (
            <>
              <DialogHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <Avatar className="h-14 w-14">
                      <AvatarFallback
                        className={`text-white text-lg ${
                          getOverallStatus(selectedPerson) === "critical"
                            ? "bg-red-500"
                            : getOverallStatus(selectedPerson) === "warning"
                              ? "bg-amber-500"
                              : "bg-green-500"
                        }`}
                      >
                        {getInitials(selectedPerson.name)}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <DialogTitle className="font-heading text-xl">
                        {selectedPerson.name}
                      </DialogTitle>
                      <p className="text-sm text-muted-foreground">
                        {selectedPerson.email}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEdit(selectedPerson);
                        setSelectedPerson(null);
                      }}
                      data-testid="edit-person-btn"
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCheckBreach(selectedPerson.id);
                      }}
                      disabled={checkingBreach === selectedPerson.id}
                      data-testid="check-breach-btn"
                    >
                      {checkingBreach === selectedPerson.id ? (
                        <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                      ) : (
                        <RefreshCw className="w-4 h-4 mr-1" />
                      )}
                      Check Breach
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(selectedPerson.id);
                      }}
                      data-testid="delete-person-btn"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </DialogHeader>

              <div className="space-y-6 mt-4">
                {/* Password Health */}
                <div>
                  <h3 className="font-semibold flex items-center gap-2 mb-3">
                    <Key className="w-4 h-4" />
                    Password Health
                  </h3>
                  <Card
                    className={`${
                      selectedPerson.passwordHealth?.is_leaked
                        ? "border-red-300 bg-red-50"
                        : selectedPerson.passwordHealth?.risk_level ===
                              "high" ||
                            selectedPerson.passwordHealth?.risk_level ===
                              "critical"
                          ? "border-amber-300 bg-amber-50"
                          : "border-green-300 bg-green-50"
                    }`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium">Status</span>
                        <Badge
                          className={`${
                            selectedPerson.passwordHealth?.is_leaked
                              ? "bg-red-100 text-red-800"
                              : selectedPerson.passwordHealth?.risk_level ===
                                    "high" ||
                                  selectedPerson.passwordHealth?.risk_level ===
                                    "critical"
                                ? "bg-amber-100 text-amber-800"
                                : "bg-green-100 text-green-800"
                          }`}
                        >
                          {selectedPerson.passwordHealth?.is_leaked
                            ? "LEAKED"
                            : selectedPerson.passwordHealth?.risk_level?.toUpperCase()}
                        </Badge>
                      </div>
                      {selectedPerson.passwordHealth?.is_leaked && (
                        <div className="text-sm text-red-700 mb-2">
                          <strong>Found in:</strong>{" "}
                          {selectedPerson.passwordHealth.leak_source}
                        </div>
                      )}
                      <div className="text-sm text-muted-foreground mb-2">
                        <strong>Password Age:</strong>{" "}
                        {selectedPerson.passwordHealth?.password_age_days} days
                      </div>
                      <div className="text-sm text-muted-foreground mb-2">
                        <strong>Strength:</strong>{" "}
                        {selectedPerson.passwordHealth?.strength}
                      </div>
                      <div className="text-sm font-medium mt-3 p-2 bg-white/50 rounded">
                        <strong>Recommendation:</strong>{" "}
                        {selectedPerson.passwordHealth?.recommendation}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Devices */}
                <div>
                  <h3 className="font-semibold flex items-center gap-2 mb-3">
                    <Monitor className="w-4 h-4" />
                    Devices ({selectedPerson.devices?.length || 0})
                  </h3>
                  {selectedPerson.devices?.length > 0 ? (
                    <div className="space-y-3">
                      {selectedPerson.devices.map((device, idx) => {
                        const health = selectedPerson.health?.find(
                          (h) => h.device_id === device.id,
                        );
                        const backup = selectedPerson.backups?.find(
                          (b) => b.device_id === device.id,
                        );
                        return (
                          <Card
                            key={idx}
                            className={`${
                              health?.health_status === "critical"
                                ? "border-red-300"
                                : health?.health_status === "warning"
                                  ? "border-amber-300"
                                  : ""
                            }`}
                          >
                            <CardContent className="p-4">
                              <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-2">
                                  <Monitor className="w-4 h-4" />
                                  <span className="font-medium">
                                    {device.name}
                                  </span>
                                </div>
                                <Badge
                                  className={`${
                                    device.status === "online"
                                      ? "bg-green-100 text-green-800"
                                      : "bg-red-100 text-red-800"
                                  }`}
                                >
                                  {device.status}
                                </Badge>
                              </div>

                              {health && (
                                <div className="space-y-2 mb-3">
                                  <div className="flex items-center justify-between text-sm">
                                    <span className="flex items-center gap-1">
                                      <Cpu className="w-3 h-3" /> CPU
                                    </span>
                                    <span>{health.cpu_usage}%</span>
                                  </div>
                                  <Progress
                                    value={health.cpu_usage}
                                    className={`h-1.5 ${health.cpu_usage > 80 ? "[&>div]:bg-red-500" : ""}`}
                                  />

                                  <div className="flex items-center justify-between text-sm">
                                    <span className="flex items-center gap-1">
                                      <MemoryStick className="w-3 h-3" /> Memory
                                    </span>
                                    <span>{health.memory_usage}%</span>
                                  </div>
                                  <Progress
                                    value={health.memory_usage}
                                    className={`h-1.5 ${health.memory_usage > 80 ? "[&>div]:bg-red-500" : ""}`}
                                  />

                                  <div className="flex items-center justify-between text-sm">
                                    <span className="flex items-center gap-1">
                                      <HardDrive className="w-3 h-3" /> Storage
                                    </span>
                                    <span>
                                      {health.disk_used_gb}GB /{" "}
                                      {health.disk_total_gb}GB (
                                      {health.disk_usage}%)
                                    </span>
                                  </div>
                                  <Progress
                                    value={health.disk_usage}
                                    className={`h-1.5 ${health.disk_usage > 80 ? "[&>div]:bg-red-500" : ""}`}
                                  />
                                </div>
                              )}

                              {backup && (
                                <div
                                  className={`flex items-center justify-between p-2 rounded text-sm ${
                                    backup.status === "failed"
                                      ? "bg-red-100"
                                      : "bg-green-100"
                                  }`}
                                >
                                  <span className="flex items-center gap-1">
                                    <Database className="w-3 h-3" />
                                    Backup
                                  </span>
                                  <span
                                    className={
                                      backup.status === "failed"
                                        ? "text-red-700"
                                        : "text-green-700"
                                    }
                                  >
                                    {backup.status === "failed"
                                      ? `Failed: ${backup.error_message}`
                                      : "OK"}
                                  </span>
                                </div>
                              )}
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  ) : (
                    <Card className="border-dashed">
                      <CardContent className="p-4 text-center text-muted-foreground">
                        No devices assigned to this person
                      </CardContent>
                    </Card>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
